// Enhanced geocoding service with BigDataCloud and Nominatim fallback
// Multi-threaded with local storage caching

interface GeocodingResult {
  name: string;
  confidence: number;
  provider: string;
  cached?: boolean;
  coordinates: [number, number];
}

interface PendingRequest {
  promise: Promise<string>;
  resolvers: Array<(value: string) => void>;
}

class GeocodingService {
  private cache = new Map<string, { result: string; timestamp: number }>();
  private readonly CACHE_DURATION = 7 * 24 * 60 * 60 * 1000;
  private readonly CACHE_KEY = 'dive_geocoding_cache';
  
  // Request deduplication and batching
  private pendingRequests = new Map<string, PendingRequest>();
  private readonly MAX_CONCURRENT_REQUESTS = 5;
  private activeRequests = 0;
  private requestQueue: Array<() => void> = [];

  constructor() {
    this.loadCache();
  }

  private loadCache(): void {
    try {
      const stored = localStorage.getItem(this.CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const now = Date.now();
        
        Object.entries(parsed).forEach(([key, entry]: [string, any]) => {
          if (entry.timestamp && now - entry.timestamp < this.CACHE_DURATION) {
            this.cache.set(key, entry);
          }
        });
      }
    } catch (error) {
      console.warn('Failed to load cache:', error);
    }
  }

  private saveCache(): void {
    try {
      const cacheObject: any = {};
      this.cache.forEach((entry, key) => {
        cacheObject[key] = entry;
      });
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheObject));
    } catch (error) {
      console.warn('Failed to save cache:', error);
    }
  }

  public clearAllCache(): void {
    this.cache.clear();
    localStorage.removeItem(this.CACHE_KEY);
  }

  public getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  private getCacheKey(lat: number, lng: number): string {
    if (lat == null || lng == null) {
      throw new Error('Invalid coordinates for cache key');
    }
    return `${lat.toFixed(3)},${lng.toFixed(3)}`;
  }

  public async getLocationName(lat: number, lng: number): Promise<string> {
    const cacheKey = this.getCacheKey(lat, lng);
    
    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached.result;
    }

    // Check if we already have a pending request for this location
    const pending = this.pendingRequests.get(cacheKey);
    if (pending) {
      return new Promise((resolve) => {
        pending.resolvers.push(resolve);
      });
    }

    // Create a new pending request
    const resolvers: Array<(value: string) => void> = [];
    const promise = this.fetchLocationName(lat, lng, cacheKey, resolvers);
    
    this.pendingRequests.set(cacheKey, { promise, resolvers });
    
    return promise;
  }

  private async fetchLocationName(
    lat: number, 
    lng: number, 
    cacheKey: string,
    resolvers: Array<(value: string) => void>
  ): Promise<string> {
    // Wait if we've hit the concurrent request limit
    if (this.activeRequests >= this.MAX_CONCURRENT_REQUESTS) {
      await new Promise<void>((resolve) => {
        this.requestQueue.push(resolve);
      });
    }

    this.activeRequests++;

    try {
      // Try BigDataCloud first
      let result: string | null = await this.tryBigDataCloud(lat, lng);

      if (!result) {
        // Fall back to Nominatim
        result = await this.tryNominatim(lat, lng);
      }

      const finalResult = result || `${lat.toFixed(4)}°, ${lng.toFixed(4)}°`;

      // Cache the result
      const cacheEntry = { result: finalResult, timestamp: Date.now() };
      this.cache.set(cacheKey, cacheEntry);
      this.saveCache();

      // Resolve all pending requests for this location
      resolvers.forEach(resolve => resolve(finalResult));

      return finalResult;

    } catch (error) {
      console.error('Geocoding error:', error);
      const fallback = `${lat.toFixed(4)}°, ${lng.toFixed(4)}°`;
      
      // Resolve all pending requests with fallback
      resolvers.forEach(resolve => resolve(fallback));
      
      return fallback;
    } finally {
      this.activeRequests--;
      this.pendingRequests.delete(cacheKey);
      
      // Process next queued request if any
      const nextRequest = this.requestQueue.shift();
      if (nextRequest) {
        nextRequest();
      }
    }
  }

  private async tryBigDataCloud(lat: number, lng: number): Promise<string | null> {
    try {
      const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { 'Accept': 'application/json' }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      return this.formatBigDataCloudResult(data, lat, lng);
      
    } catch (error) {
      console.warn('⚠️ BigDataCloud failed:', error);
      return null;
    }
  }

  private formatBigDataCloudResult(data: any, lat: number, lng: number): string {
    if (data.ocean) {
      const parts = [data.ocean];
      if (data.locality || data.city) {
        parts.push(`(${data.locality || data.city})`);
      } else if (data.principalSubdivision) {
        parts.push(`(${data.principalSubdivision})`);
      }
      return parts.join(' ');
    }
    
    const parts: string[] = [];
    
    if (data.locality) {
      parts.push(data.locality);
    } else if (data.city) {
      parts.push(data.city);
    }
    
    if (data.principalSubdivision && data.principalSubdivisionCode) {
      if (data.countryName === 'United States of America' && data.principalSubdivisionCode.length === 2) {
        parts.push(data.principalSubdivisionCode);
      } else {
        parts.push(data.principalSubdivision);
      }
    }
    
    return parts.join(', ') || data.countryName || 'Unknown Location';
  }

  private async tryNominatim(lat: number, lng: number): Promise<string | null> {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=14&addressdetails=1`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      
      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'User-Agent': 'NewAchievementScuba/1.0 (jjenkinsmedina@gmail.com)',
          'Accept': 'application/json'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      return this.formatNominatimResult(data);
      
    } catch (error) {
      console.warn('⚠️ Nominatim failed:', error);
      return null;
    }
  }

  private formatNominatimResult(data: any): string {
    if (!data.address) {
      return data.display_name || 'Unknown Location';
    }
    
    const addr = data.address;
    const parts: string[] = [];
    
    if (addr.body_of_water || addr.water) {
      parts.push(addr.body_of_water || addr.water);
    }
    
    if (addr.town || addr.city) {
      parts.push(addr.town || addr.city);
    } else if (addr.county && !parts.length) {
      parts.push(`${addr.county} Coast`);
    }
    
    if (addr.state && addr.country === 'United States') {
      parts.push(this.getStateAbbrev(addr.state));
    }
    
    return parts.filter(Boolean).join(', ') || data.display_name || 'Unknown Location';
  }

  private getStateAbbrev(stateName: string): string {
    const states: { [key: string]: string } = {
      'Florida': 'FL', 'California': 'CA', 'Hawaii': 'HI'
    };
    return states[stateName] || stateName;
  }

  public async geocodeLocations(locations: Array<{ lat: number; lng: number }>): Promise<GeocodingResult[]> {
    // Process all locations in parallel without rate limiting delays
    // The MAX_CONCURRENT_REQUESTS will handle throttling automatically
    const promises = locations.map(async (location) => {
      try {
        const name = await this.getLocationName(location.lat, location.lng);
        const cached = this.cache.has(this.getCacheKey(location.lat, location.lng));

        return {
          name,
          confidence: 0.8,
          provider: 'multi-provider',
          cached,
          coordinates: [location.lat, location.lng] as [number, number]
        };
      } catch (error) {
        console.error(`Failed to geocode ${location.lat}, ${location.lng}:`, error);
        return {
          name: `${location.lat.toFixed(4)}°, ${location.lng.toFixed(4)}°`,
          confidence: 0,
          provider: 'coordinates',
          cached: false,
          coordinates: [location.lat, location.lng] as [number, number]
        };
      }
    });

    const results = await Promise.all(promises);
    return results;
  }

  // Prefetch locations for improved UX
  public prefetchLocations(locations: Array<{ lat: number; lng: number }>): void {
    // Filter out already cached locations and invalid coordinates
    const uncachedLocations = locations.filter(loc => {
      if (loc.lat == null || loc.lng == null) {
        return false; // Skip invalid coordinates
      }
      try {
        const cacheKey = this.getCacheKey(loc.lat, loc.lng);
        return !this.cache.has(cacheKey);
      } catch (error) {
        console.warn('Invalid location for prefetch:', loc);
        return false;
      }
    });

    if (uncachedLocations.length === 0) {
      return;
    }

    // Fire and forget - don't await
    this.geocodeLocations(uncachedLocations).catch(error => {
      console.warn('Prefetch error (non-critical):', error);
    });
  }

  // Forward geocoding - search for locations by name (via backend proxy)
  public async searchLocations(query: string): Promise<LocationSearchResult[]> {
    if (!query || query.trim().length < 3) {
      return [];
    }

    try {
      // Use backend proxy to avoid CORS issues
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const url = `${apiUrl}/api/dives/search-locations?query=${encodeURIComponent(query)}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json'
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const results: LocationSearchResult[] = await response.json();
      return results;

    } catch (error) {
      console.error('Location search error:', error);
      return [];
    }
  }
}

export interface LocationSearchResult {
  displayName: string;
  latitude: number;
  longitude: number;
  type: string;
  address: {
    city?: string;
    state?: string;
    country?: string;
    water?: string;
  };
}

export const geocodingService = new GeocodingService();
